import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MyContext } from '../../context';
import StatCard from '../../components/StatCard';
import EmptyState from '../../components/empty-state';
import Badge from '../../components/badge';
import { getCommandCenterSummary, listAttentionQueueItems, dismissAttentionQueueItem } from '../../utils/queries';

const IMPACT_BADGE = {
	High: { bgColor: 'bg-red-700', textColor: 'text-white', border: 'border-red-700' },
	Medium: { bgColor: 'bg-amber-600', textColor: 'text-white', border: 'border-amber-600' },
	Low: { bgColor: 'bg-slate-600', textColor: 'text-white', border: 'border-slate-600' },
};

// These categories have no (or incomplete) automated closing event -- see
// attentionRules.service.ts -- so they get an expandable per-item Resolve
// action alongside the grouped count, instead of relying purely on events
// to clear them.
const MANUALLY_RESOLVABLE_TYPES = new Set(['PAYMENT_FAILURE', 'ORDER_EXCEPTION']);

const POLL_INTERVAL_MS = 12000;

const currency = (value) =>
	typeof value === 'number'
		? value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
		: '-';

const points = (value) => (typeof value === 'number' ? `${Math.round(value).toLocaleString()} pts` : '-');

// Used by the Fulfillment Rate card, currently commented out below.
// eslint-disable-next-line no-unused-vars
const percent = (value) => (typeof value === 'number' ? `${Math.round(value * 100)}%` : '-');

function ResolvableItemsPanel({ storeId, queueType, onResolved }) {
	const [items, setItems] = useState(null);
	const [resolvingId, setResolvingId] = useState(null);

	useEffect(() => {
		listAttentionQueueItems({ storeId, queueType }).then((result) => {
			if (result?.success) setItems(result.data.items);
		});
	}, [storeId, queueType]);

	const handleResolve = async (item) => {
		setResolvingId(item.attentionQueueItemId);
		const result = await dismissAttentionQueueItem(item.attentionQueueItemId, storeId);
		setResolvingId(null);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to resolve.');
			return;
		}
		setItems((current) => current.filter((i) => i.attentionQueueItemId !== item.attentionQueueItemId));
		onResolved?.();
	};

	if (!items) return <p className='text-xs text-gray-400 px-4 py-3'>Loading...</p>;
	if (items.length === 0) return <p className='text-xs text-gray-400 px-4 py-3'>Nothing left to resolve.</p>;

	return (
		<ul className='divide-y divide-gray-100'>
			{items.map((item) => (
				<li key={item.attentionQueueItemId} className='flex items-center justify-between px-4 py-2'>
					<span className='text-xs text-gray-600'>
						{item.payload?.order_number ? `Order #${item.payload.order_number}` : `${item.entityType} #${item.entityId}`}
						{item.payload?.reason ? ` — ${item.payload.reason}` : ''}
					</span>
					<button
						type='button'
						className='btn-outline-small disabled:opacity-50'
						disabled={resolvingId === item.attentionQueueItemId}
						onClick={() => handleResolve(item)}
					>
						{resolvingId === item.attentionQueueItemId ? 'Resolving...' : 'View'}
					</button>
				</li>
			))}
		</ul>
	);
}

export default function CommandCenter() {
	const { activeStoreId } = useContext(MyContext);
	const navigate = useNavigate();
	const [summary, setSummary] = useState(null);
	const [loading, setLoading] = useState(true);
	const [expandedType, setExpandedType] = useState(null);
	const pollRef = useRef(null);

	const load = (showSpinner) => {
		if (!activeStoreId) return;
		if (showSpinner) setLoading(true);
		getCommandCenterSummary({ storeId: activeStoreId }).then((result) => {
			if (result?.success) setSummary(result.data);
			if (showSpinner) setLoading(false);
		});
	};

	useEffect(() => {
		load(true);
		// Polling, not push -- there's no WebSocket/SSE transport in this app
		// today. An item vanishing from the list on its next poll (auto-closed
		// by a rule, or manually resolved) is the whole "real-time" effect for
		// this v1.
		pollRef.current = setInterval(() => load(false), POLL_INTERVAL_MS);
		return () => clearInterval(pollRef.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStoreId]);

	const needsAttention = (summary?.needs_attention || []).filter((row) => row.count > 0);
	const business = summary?.todays_business;
	const formatImpact = summary?.impact_unit === 'points' ? points : currency;
	// null unless the store has "Manual review of each order is required" on.
	const showPendingReviews = business?.pending_reviews != null;

	return (
		<div className='py-8 pr-8'>
			<h1 className='text-2xl font-light tracking-tight mb-6'>Today</h1>

			{loading || !summary ? (
				<div className='text-sm text-gray-400'>Loading...</div>
			) : (
				<>
					<h2 className='text-sm font-medium text-gray-900 mb-3'>Overview</h2>
					<div className='grid grid-cols-3 lg:grid-cols-6 gap-4'>
						<StatCard label='New Orders' variant='green' value={business?.orders_count ?? 0} />
						<StatCard label='Orders Total' value={formatImpact(business?.orders_total)} />
						{/* <StatCard label='Fulfillment Rate' value={percent(business?.fulfillment_rate)} /> */}
						<StatCard label='Pending Approvals' variant='slate' value={business?.pending_approvals ?? 0} />
						{showPendingReviews && <StatCard label='Pending Reviews' variant='slate' value={business.pending_reviews} />}
						<StatCard label='Order Exceptions' variant="dark" value={business?.order_exceptions ?? 0} />
						<StatCard label='Inventory Risks' value={business?.inventory_risks ?? 0} />
					</div>
				
					<div className='mt-14 flex items-center gap-2 mb-3'>
						<h2 className='text-sm font-medium text-gray-900'>Needs Attention</h2>
						{needsAttention.length > 0 && (
							<Badge
								text={String(needsAttention.reduce((n, r) => n + r.count, 0))}
								bgColor='bg-black'
								textColor='text-white'
								border=''
							/>
						)}
					</div>
					{needsAttention.length === 0 ? (
						<div className='mb-8 border border-gray-200 rounded-lg'>
							<EmptyState heading="You're all caught up" subhead='Nothing needs your attention right now.' size='sm' />
						</div>
					) : (
						<div className='mb-8 border border-gray-200 rounded-lg overflow-hidden'>
							<table className='min-w-full divide-y divide-gray-200'>
								<thead className='bg-gray-50'>
									<tr>
										<th className='text-left text-xs font-medium text-gray-500 uppercase px-4 py-2'>Issue</th>
										<th className='text-left text-xs font-medium text-gray-500 uppercase px-4 py-2'>Impact</th>
										<th className='px-4 py-2'></th>
									</tr>
								</thead>
								<tbody className='divide-y divide-gray-100'>
									{needsAttention.map((row) => {
										const resolvable = MANUALLY_RESOLVABLE_TYPES.has(row.type);
										const expanded = expandedType === row.type;
										return (
											<Fragment key={row.type}>
												<tr>
													<td className='px-4 py-3 text-sm text-gray-900'>
														{row.count} {row.label}
													</td>
													<td className='px-4 py-3'>
														{row.impact_amount != null ? (
															<span className='text-sm text-gray-900 font-medium'>{formatImpact(row.impact_amount)}</span>
														) : (
															<Badge text={row.impact} {...(IMPACT_BADGE[row.impact] || IMPACT_BADGE.Low)} />
														)}
													</td>
													<td className='px-4 py-3 text-right space-x-3'>
														{resolvable ? (
															<button
																type='button'
																className='btn-outline-small'
																onClick={() => setExpandedType(expanded ? null : row.type)}
															>
																{expanded ? 'Hide' : row.action}
															</button>
														) : (
															<button
																type='button'
																className='btn-outline-small'
																onClick={() => navigate(row.link)}
															>
																{row.action}
															</button>
														)}
													</td>
												</tr>
												{expanded && (
													<tr>
														<td colSpan={3} className='bg-gray-50 p-0'>
															<ResolvableItemsPanel
																storeId={activeStoreId}
																queueType={row.type}
																onResolved={() => load(false)}
															/>
														</td>
													</tr>
												)}
											</Fragment>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</>
			)}
		</div>
	);
}
